// Copyright 2017 Sorama B.V.

import { bindable, BindingEngine, Disposable, inject } from "aurelia-framework";
import { SelectableListBase } from "controls/selectable-list-base/SelectableListBase";
import { IFilter } from "filters/IFilter";
import { IGenericRepository } from "repositories/IGenericRepository";
import { MeasurementSetupRepository } from "repositories/MeasurementSetupRepository";
import { MeasurementDevice } from "services/Hardware/Datatypes/MeasurementDevice";
import { MeasurementDeviceSetup, MeasurementDeviceSetupInfo } from "services/Hardware/Datatypes/MeasurementDeviceSetup";

@inject(Element, MeasurementSetupRepository, "IGenericRepository<MeasurementDevice,MeasurementDevice>", BindingEngine)
export class MeasurementDeviceList extends SelectableListBase<MeasurementDeviceSetupInfo, { id: string }> {
    //#region Injected Services
    private measurementSetupRepository: MeasurementSetupRepository;
    private measurementDeviceRepository: IGenericRepository<MeasurementDevice, MeasurementDevice>;
    //#endregion

    //#region SelectableListBase Properties
    @bindable
    public selectedItemId: string;

    @bindable
    public checkedItemIds: string[] = [];

    @bindable
    public filter: IFilter<MeasurementDeviceSetupInfo>;

    @bindable
    public canAdd: boolean;
    //#endregion

    @bindable
    public measurementDeviceSetupId: string;

    //#region Private Properties
    private measurementDeviceSetup: MeasurementDeviceSetup;
    private subscriptions: Disposable[];
    //#endregion

    constructor(
        element: Element,
        measurementSetupRepository: MeasurementSetupRepository,
        measurementDeviceRepository: IGenericRepository<MeasurementDevice, MeasurementDevice>,
        bindingEngine: BindingEngine
    ) {
        super(element, bindingEngine);
        this.measurementSetupRepository = measurementSetupRepository;
        this.measurementDeviceRepository = measurementDeviceRepository;
    }

    public bind(): void {
        super.bind();

        this.subscriptions = [
            this.bindingEngine
                .collectionObserver(this.measurementSetupRepository.items)
                .subscribe(() => this.measurementDeviceSetupIdChanged()),
        ];

        this.measurementDeviceSetupIdChanged();
    }

    public unbind(): void {
        while (this.subscriptions.length !== 0) {
            this.subscriptions.pop().dispose();
        }

        super.unbind();
    }

    //#region Property Changed Handlers
    public measurementDeviceSetupIdChanged(): void {
        if (this.measurementDeviceSetupId) {
            this.measurementSetupRepository.getById(this.measurementDeviceSetupId).then((x) => {
                return MeasurementDeviceSetup.fromRepository(x, this.measurementDeviceRepository)
                    .then((setup) => {
                        this.measurementDeviceSetup = setup;
                        this.refresh();
                    })
                    .catch(() => {
                        this.measurementDeviceSetup = undefined;
                        this.refresh();
                    });
            });
        } else {
            this.measurementDeviceSetup = null;
        }
    }
    //#endregion

    //#region SelectableListBase Members
    protected getItems(): Promise<MeasurementDeviceSetup> {
        return this.measurementSetupRepository.getById(this.measurementDeviceSetupId).then((x) => {
            return MeasurementDeviceSetup.fromRepository(x, this.measurementDeviceRepository);
        });
    }

    protected getChildItemsFromItem(item: MeasurementDeviceSetupInfo): Promise<{ id: string }[]> {
        return Promise.resolve([]);
    }

    protected deleteItem(itemId: string): Promise<void> {
        if (!this.measurementDeviceSetup) return Promise.resolve();

        const itemIdx = this.measurementDeviceSetup.findIndex((x) => x.id === itemId);

        let updatePromise: Promise<void>;
        if (itemIdx !== -1) {
            this.measurementDeviceSetup.splice(itemIdx, 1);
            const stub = MeasurementDeviceSetup.toStub(this.measurementDeviceSetup);

            updatePromise = this.measurementSetupRepository.put(stub).then(() => {});
        } else {
            updatePromise = Promise.resolve();
        }

        return updatePromise.then(() => {
            if (itemId === this.selectedItemId) this.selectedItemId = null;
        });
    }

    protected deleteChildItem(childItemId: string, itemId: string): Promise<void> {
        // Not used
        return Promise.resolve();
    }
    //#endregion
}
